import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage, FormattedDate } from 'dibs-react-intl';

import { NftItemTileTooltip } from '../NftItemTileTooltip/NftItemTileTooltip.tsx';

import { Button } from 'dibs-elements/exports/Button.tsx';

// reuse NftItemTileDetails styling
import styles from '../styles/NftItemTileItemDetails.scss';
// reuse tileContent styling
import tileStyles from '../nftItemTileContent/styles/nftItemTileContent.scss';

import { type NftDraftItemTileDetails_viewer$key } from './__generated__/NftDraftItemTileDetails_viewer.graphql.ts';
import { type NftDraftItemTileDetails_nftItemDraft$key } from './__generated__/NftDraftItemTileDetails_nftItemDraft.graphql.ts';

type Props = {
    viewer: NftDraftItemTileDetails_viewer$key;
    nftItemDraft: NftDraftItemTileDetails_nftItemDraft$key;
    showCreator?: boolean;
    fireTracking?: () => void;
};

export const NftDraftItemTileDetails: FC<Props> = ({
    viewer: viewerRef,
    nftItemDraft: nftItemDraftRef,
}) => {
    const viewer = useFragment(
        graphql`
            fragment NftDraftItemTileDetails_viewer on Viewer {
                ...NftItemTileTooltip_viewer
            }
        `,
        viewerRef
    );
    const nftItemDraft = useFragment(
        graphql`
            fragment NftDraftItemTileDetails_nftItemDraft on NftItemDraft {
                serviceId
                name
                editionNumber
                editionTotal
                createdDate
                modifiedDate
                ...NftItemTileTooltip_nftItemDraft
            }
        `,
        nftItemDraftRef
    );

    const { serviceId, name, editionTotal, createdDate, modifiedDate } = nftItemDraft;
    const edition = editionTotal || (
        <FormattedMessage id="nftDraftTile.details.unset" defaultMessage="unset" />
    );

    return (
        <div className={tileStyles.collectorDetails}>
            <div className={tileStyles.contentTopContainer}>
                <div>
                    <p className={styles.title}>
                        {name || (
                            <FormattedMessage
                                id="nftDraftTile.details.untitled"
                                defaultMessage="Untitled"
                            />
                        )}
                    </p>

                    <FormattedMessage
                        id="nftDraftTile.details.edition"
                        defaultMessage="edition: {edition}"
                        values={{
                            edition,
                        }}
                    />
                </div>
                <NftItemTileTooltip viewer={viewer} nftItem={null} nftItemDraft={nftItemDraft} />
            </div>

            <div className={tileStyles.detailsSection}>
                <p>
                    {modifiedDate && modifiedDate !== createdDate ? (
                        <FormattedMessage
                            id="nftDraftTile.details.edited"
                            defaultMessage="<bold>Edited:</bold> {timestamp}"
                            values={{
                                bold: chunks => <span className={tileStyles.bold}>{chunks}</span>,
                                timestamp: (
                                    <FormattedDate
                                        year="numeric"
                                        month="long"
                                        day="2-digit"
                                        hour="2-digit"
                                        minute="2-digit"
                                        value={modifiedDate}
                                    />
                                ),
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="nftDraftTile.details.created"
                            defaultMessage="<bold>Created:</bold> {timestamp}"
                            values={{
                                bold: chunks => <span className={tileStyles.bold}>{chunks}</span>,
                                timestamp: (
                                    <FormattedDate
                                        year="numeric"
                                        month="long"
                                        day="2-digit"
                                        hour="2-digit"
                                        minute="2-digit"
                                        value={createdDate ?? ''}
                                    />
                                ),
                            }}
                        />
                    )}
                </p>
            </div>

            <Button
                href={`/nft-create/${serviceId}`}
                size="medium"
                fullWidth
                className={tileStyles.button}
            >
                <FormattedMessage id="nftDraftTile.details.editNft" defaultMessage="Edit NFT" />
            </Button>
        </div>
    );
};
